import { useSyncExternalStore } from 'react';
import { getDoc, setDoc } from './store';
import type { BlocoPag, Docs, Fichario, Folha, Materia, Pagina } from './types';
import { addDays, parseYmd, today, uid, weekStart, ymd } from '../lib/date';
import { t } from '../lib/i18n';
import { nomeAnexo } from '../lib/nomeArquivo';

// ─── Fichários, matérias e páginas ────────────────────────────────────────────────────────────
type D = Docs['estudos'];
const mut = (fn: (d: D) => D) => setDoc('estudos', fn);
const mapMat = (fid: string, mid: string, fn: (m: Materia) => Materia) =>
  mut(d => ({ ...d, ficharios: d.ficharios.map(f => f.id !== fid ? f : { ...f, materias: f.materias.map(m => (m.id === mid ? fn(m) : m)) }) }));

export function salvarFichario(f: { id: string | null; nome: string; icon: string; cor: string | null }) {
  mut(d => f.id
    ? { ...d, ficharios: d.ficharios.map(x => (x.id === f.id ? { ...x, nome: f.nome, icon: f.icon, cor: f.cor } : x)) }
    : { ...d, ficharios: [...d.ficharios, { id: uid(), nome: f.nome, icon: f.icon, cor: f.cor, materias: [] }] });
}
export const excluirFichario = (id: string) => mut(d => ({ ...d, ficharios: d.ficharios.filter(f => f.id !== id) }));

export function salvarMateria(fid: string, m: { id: string | null; nome: string; icon: string; categoria?: string }) {
  if (m.id) return mapMat(fid, m.id, x => ({ ...x, nome: m.nome, icon: m.icon, categoria: m.categoria ?? x.categoria }));
  mut(d => ({ ...d, ficharios: d.ficharios.map(f => f.id !== fid ? f
    : { ...f, materias: [...f.materias, { id: uid(), nome: m.nome, icon: m.icon, paginas: [], metaMin: 0, ...(m.categoria ? { categoria: m.categoria } : {}) }] }) }));
}
export const excluirMateria = (fid: string, mid: string) =>
  mut(d => ({ ...d, ficharios: d.ficharios.map(f => f.id !== fid ? f : { ...f, materias: f.materias.filter(m => m.id !== mid) }) }));
export const metaMateria = (fid: string, mid: string, metaMin: number, metaPor?: 'dia' | 'semana') => mapMat(fid, mid, m => ({ ...m, metaMin, ...(metaPor ? { metaPor } : {}) }));
/** Meta da semana em minutos (meta por dia conta 7 dias). */
export const metaSemanal = (m: Materia) => (m.metaPor === 'dia' ? m.metaMin * 7 : m.metaMin);
/** Minutos por matéria entre duas datas. */
export function minutosEntre(de: string, ate: string, sessoes = getDoc('estudos').sessoes) {
  const r: Record<string, number> = {};
  for (const s of sessoes) if (s.data >= de && s.data <= ate) r[s.materiaId] = (r[s.materiaId] ?? 0) + s.min;
  return r;
}
export const segundaDe = (d: string) => segunda(d);
/** 0.72 (texto 11e D6): meta da matéria convertida para um período de N dias.
 *  Meta por dia × N (semana = ×7; mês = × dias do mês). Meta por semana ÷ 7 × N (semana = ela mesma; mês = × dias/7). */
export const metaNoPeriodo = (m: Materia, dias: number) => Math.round(m.metaPor === 'dia' ? m.metaMin * dias : (m.metaMin / 7) * dias);

// ---------- arrastar para reorganizar (0.34) ----------
export const ordenarFicharios = (lista: Fichario[]) => mut(d => ({ ...d, ficharios: lista }));
export const ordenarMaterias = (fid: string, lista: Materia[]) =>
  mut(d => ({ ...d, ficharios: d.ficharios.map(f => (f.id === fid ? { ...f, materias: lista } : f)) }));
/** Leva a matéria (com as páginas) para outro fichário, no fim da lista. */
export function moverMateria(deFid: string, mid: string, paraFid: string) {
  if (deFid === paraFid) return;
  mut(d => {
    const m = d.ficharios.find(f => f.id === deFid)?.materias.find(x => x.id === mid);
    if (!m || !d.ficharios.some(f => f.id === paraFid)) return d;
    return { ...d, ficharios: d.ficharios.map(f => f.id === deFid ? { ...f, materias: f.materias.filter(x => x.id !== mid) }
      : f.id === paraFid ? { ...f, materias: [...f.materias, m] } : f) };
  });
}
export const ordenarPaginas = (fid: string, mid: string, lista: Pagina[]) => mapMat(fid, mid, m => ({ ...m, paginas: lista }));
/** Leva a página para outra matéria (de qualquer fichário). */
export function moverPagina(fid: string, mid: string, pid: string, paraMid: string) {
  if (mid === paraMid) return;
  mut(d => {
    const p = d.ficharios.find(f => f.id === fid)?.materias.find(m => m.id === mid)?.paginas.find(x => x.id === pid);
    if (!p) return d;
    return { ...d, ficharios: d.ficharios.map(f => ({ ...f, materias: f.materias.map(m => m.id === mid ? { ...m, paginas: m.paginas.filter(x => x.id !== pid) }
      : m.id === paraMid ? { ...m, paginas: [...m.paginas, p] } : m) })) };
  });
}
export const ordenarArquivos = (fid: string, mid: string, chaves: string[]) => mapMat(fid, mid, m => ({ ...m, ordemArq: chaves }));

/** Texto puro dos blocos (prévia na lista e compatibilidade com versões antigas do app). */
export function textoDosBlocos(bs: BlocoPag[]) {
  return bs.map(b => {
    switch (b.tipo) {
      case 'texto': return b.texto;
      case 'check': return b.itens.map(i => (i.done ? '☑ ' : '☐ ') + i.text).join('\n');
      case 'lista': return b.itens.map(i => '• ' + i.text).join('\n');
      case 'tabela': return b.linhas.filter(l => l.some(c => c.trim())).map(l => l.join(' | ')).join('\n');
      case 'link': return `🔗 ${b.titulo ? b.titulo + ' ' : ''}${b.url}`;
      case 'imagem': return `🖼️ ${nomeAnexo(b.arq)}`;
      case 'arquivo': return `📎 ${nomeAnexo(b.arq)}${b.arq.url ? ' ' + b.arq.url : ''}`;
      case 'desenho': return '✍️';
    }
  }).filter(x => x && x.trim()).join('\n\n');
}
/** Blocos da página. Páginas antigas (só texto) viram um bloco de texto. */
export const blocosDaPagina = (p: Pick<Pagina, 'texto' | 'blocos'>): BlocoPag[] =>
  p.blocos ?? (p.texto ? [{ id: uid(), tipo: 'texto', texto: p.texto }] : []);

export function salvarPagina(fid: string, mid: string, p: Partial<Pagina> & { titulo: string; blocos: BlocoPag[] }) {
  const agora = new Date().toISOString();
  // o que foi escrito no quadro também entra no resumo da página (busca e prévia na lista)
  const doQuadro = p.quadro ? [...p.quadro.textos.map(x => x.texto), ...p.quadro.checks.flatMap(c => c.itens.map(i => i.text))].filter(x => x.trim()).join('\n') : '';
  const texto = [textoDosBlocos(p.blocos), doQuadro].filter(Boolean).join('\n');
  mapMat(fid, mid, m => p.id && m.paginas.some(x => x.id === p.id)
    ? { ...m, paginas: m.paginas.map(x => (x.id === p.id ? { ...x, titulo: p.titulo, texto, blocos: p.blocos, ...(p.quadro !== undefined ? { quadro: p.quadro } : {}), updatedAt: agora } : x)) }
    : { ...m, paginas: [...m.paginas, { id: p.id ?? uid(), titulo: p.titulo, texto, blocos: p.blocos, ...(p.quadro ? { quadro: p.quadro } : {}), revisada: false, criadoEm: agora, updatedAt: agora }] });
}
/** 0.65: "Aplicar a todas as páginas" desta matéria (tipo, cor e tema da folha). */
export const aplicarFolhaMateria = (fid: string, mid: string, folha: Folha) =>
  mapMat(fid, mid, m => ({ ...m, paginas: m.paginas.map(p => (p.quadro ? { ...p, quadro: { ...p.quadro, folha } } : p)) }));
/** Acrescenta blocos no fim de uma página (ou cria uma página nova com eles). Usado ao enviar caixas de Anotações. */
export function acrescentarNaPagina(fid: string, mid: string, pid: string | null, titulo: string, novos: BlocoPag[]) {
  const m = getDoc('estudos').ficharios.find(f => f.id === fid)?.materias.find(x => x.id === mid);
  const p = pid ? m?.paginas.find(x => x.id === pid) : undefined;
  if (p) salvarPagina(fid, mid, { id: p.id, titulo: p.titulo, blocos: [...blocosDaPagina(p), ...novos] });
  else salvarPagina(fid, mid, { titulo, blocos: novos });
}
export const revisarPagina = (fid: string, mid: string, pid: string) =>
  mapMat(fid, mid, m => ({ ...m, paginas: m.paginas.map(p => (p.id === pid ? { ...p, revisada: !p.revisada } : p)) }));
export const excluirPagina = (fid: string, mid: string, pid: string) =>
  mapMat(fid, mid, m => ({ ...m, paginas: m.paginas.filter(p => p.id !== pid) }));

export function acharMateria(mid: string): { f: Fichario; m: Materia } | null {
  for (const f of getDoc('estudos').ficharios) { const m = f.materias.find(x => x.id === mid); if (m) return { f, m }; }
  return null;
}

/** Início da semana da data (segunda ou domingo, conforme Ajustes > Calendário). */
export function segunda(s = today()) {
  return weekStart(s);
}
/** Minutos estudados por matéria na semana atual. */
export function minutosSemana(sessoes = getDoc('estudos').sessoes) {
  const ini = segunda(), fim = addDays(ini, 6);
  const r: Record<string, number> = {};
  for (const s of sessoes) if (s.data >= ini && s.data <= fim) r[s.materiaId] = (r[s.materiaId] ?? 0) + s.min;
  return r;
}

/** Grava minutos estudados. Com id (sessão de estudo), soma no mesmo registro em vez de criar outro. */
export function registrarSessao(materiaId: string, min: number, id?: string) {
  if (min < 1) return;
  mut(d => {
    const sessoes = d.sessoes.filter(s => s.data >= addDays(today(), -400));
    if (id && sessoes.some(s => s.id === id)) return { ...d, sessoes: sessoes.map(s => (s.id === id ? { ...s, min: s.min + min } : s)) };
    return { ...d, sessoes: [...sessoes, { id: id ?? uid(), materiaId, data: today(), min }] };
  });
}

// ─── Cronômetro de estudo (continua contando ao trocar de tela) ───────────────────────────────
export type Crono = { materiaId: string; nome: string; inicio: number | null; acumulado: number } | null;
let crono: Crono = null;
const ouvintes = new Set<() => void>();
const set = (c: Crono) => { crono = c; ouvintes.forEach(f => f()); };
export const useCrono = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => crono);
/** O cronômetro agora (fora da tela, ex.: o guia conferir se há estudo em andamento). */
export const cronoAtual = () => crono;
export const segundosCrono = (c: NonNullable<Crono>) => c.acumulado + (c.inicio ? Math.floor((Date.now() - c.inicio) / 1000) : 0);

export function iniciarEstudo(materiaId: string, nome: string) {
  if (crono && crono.materiaId !== materiaId) encerrarEstudo();
  if (!crono) set({ materiaId, nome, inicio: Date.now(), acumulado: 0 });
}
export function pausarEstudo() {
  if (!crono) return;
  set(crono.inicio ? { ...crono, inicio: null, acumulado: segundosCrono(crono) } : { ...crono, inicio: Date.now() });
}
/** Encerra e grava a sessão. Devolve os minutos registrados. */
export function encerrarEstudo() {
  if (!crono) return 0;
  const min = Math.round(segundosCrono(crono) / 60);
  registrarSessao(crono.materiaId, min);
  set(null);
  return min;
}
export const descartarEstudo = () => set(null);

/** 0.72 (texto 11e C3/C4): minutos estudados num dia, todas as matérias. O histórico já tem a sessão (lançada enquanto corre)
 *  e o cronômetro simples já encerrado; o cronômetro simples ainda ligado (só grava ao encerrar) entra pelo tempo corrido.
 *  Cada minuto conta uma vez: os dois relógios não correm juntos (0.69 F). */
export function minutosNoDia(data = today(), sessoes = getDoc('estudos').sessoes) {
  const gravado = sessoes.reduce((a, s) => a + (s.data === data ? s.min : 0), 0);
  return gravado + (crono && data === today() ? Math.floor(segundosCrono(crono) / 60) : 0);
}

// ─── duplicar (0.52): cópia logo abaixo da original ──────────────────────────────────────────
const clonar = <T,>(x: T): T => JSON.parse(JSON.stringify(x));
const copiaPag = (p: Pagina, nome?: string): Pagina => ({ ...clonar(p), id: uid(), titulo: nome ?? p.titulo, updatedAt: new Date().toISOString() });
const copiaMat = (m: Materia, nome?: string): Materia => ({ ...clonar(m), id: uid(), nome: nome ?? m.nome, paginas: m.paginas.map(p => copiaPag(p)), ordemArq: undefined });
const depois = <T extends { id: string }>(l: T[], id: string, novo: T) => { const i = l.findIndex(x => x.id === id); const c = [...l]; c.splice(i + 1, 0, novo); return c; };
export function duplicarFichario(fid: string) {
  mut(d => {
    const f = d.ficharios.find(x => x.id === fid); if (!f) return d;
    const novo: Fichario = { ...clonar(f), id: uid(), nome: t('{titulo} (cópia)', { titulo: f.nome }), materias: f.materias.map(m => copiaMat(m)) };
    return { ...d, ficharios: depois(d.ficharios, fid, novo) };
  });
}
export function duplicarMateria(fid: string, mid: string) {
  mut(d => ({ ...d, ficharios: d.ficharios.map(f => {
    if (f.id !== fid) return f;
    const m = f.materias.find(x => x.id === mid); if (!m) return f;
    return { ...f, materias: depois(f.materias, mid, copiaMat(m, t('{titulo} (cópia)', { titulo: m.nome }))) };
  }) }));
}
export function duplicarPagina(fid: string, mid: string, pid: string) {
  mapMat(fid, mid, m => {
    const p = m.paginas.find(x => x.id === pid); if (!p) return m;
    return { ...m, paginas: depois(m.paginas, pid, copiaPag(p, t('{titulo} (cópia)', { titulo: p.titulo }))) };
  });
}
